"use client";

import { useEffect, useState } from "react";
import { Col, Form, InputGroup } from "react-bootstrap";
import { useField } from "formik";

/** Convert ISO (UTC) -> "YYYY-MM-DDTHH:mm" in the user's local zone (for <input type="datetime-local">) */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const Input = ({
  name,
  mb = "mb-1",
  label,
  type = "text",
  col = 6,
  required = false,
  prefix,
  suffix,
  placeholder,
  noLabel = false,
  disabled = false,
  noFeedback = false,
  min,
  max,
  step,
  showFeedback = true,
  value, // optional controlled value
  onChange, // optional controlled onChange
  as,
  rows,
}) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;

  const isControlled = typeof value !== "undefined";
  const isDateTimeLocal = type === "datetime-local";
  const controlId = name;

  const [displayLocal, setDisplayLocal] = useState("");

  // keep a pretty local string in the field for datetime-local, while Formik stores ISO UTC
  useEffect(() => {
    if (!isDateTimeLocal) return;
    const iso = isControlled ? value : field.value;
    setDisplayLocal(isoToLocalInput(iso));
  }, [isDateTimeLocal, field.value, isControlled, value]);

  const handleChange = (e) => {
    const v = e.target.value;

    if (isDateTimeLocal) {
      setDisplayLocal(v);
      const iso = v ? new Date(v).toISOString() : "";
      if (isControlled) {
        onChange?.({ ...e, target: { ...e.target, name, value: iso } });
      } else {
        setValue(iso);
      }
      return;
    }

    // date / text / number / textarea...
    if (isControlled) onChange?.(e);
    else field.onChange(e);
  };

  const inputClass =
    prefix && suffix
      ? "rounded-0"
      : prefix
        ? "rounded-end-2 rounded-start-0"
        : suffix
          ? "rounded-start-2 rounded-end-0"
          : "rounded-2";

  const controlProps = {
    name, // required for Formik
    type,
    placeholder,
    className: inputClass,
    isInvalid: meta.touched && !!meta.error,
    autoComplete: "off",
    disabled,
    value: isDateTimeLocal
      ? displayLocal
      : isControlled
        ? value
        : (field.value ?? ""),
    onChange: handleChange,
    onBlur: field.onBlur,
    ...(type === "number" && {
      onFocus: (e) => e.target.select(),
    }),
  };

  // ✅ pass-through support for textarea without breaking anything
  if (as) controlProps.as = as;
  if (as === "textarea" && rows) controlProps.rows = rows;

  // min/max/step support
  if (["number", "date", "datetime-local"].includes(type)) {
    if (min !== undefined) {
      controlProps.min = isDateTimeLocal ? isoToLocalInput(min) || min : min;
    }
    if (max !== undefined) {
      controlProps.max = isDateTimeLocal ? isoToLocalInput(max) || max : max;
    }
  }
  if (type === "number" && step !== undefined) controlProps.step = step;
  if (type === "datetime-local" && step === undefined) controlProps.step = 60;

  return (
    <Col md={col}>
      <Form.Group controlId={controlId} className={mb}>
        {!noLabel && (
          <Form.Label className="mb-0">
            {label}
            {required && <span className="text-danger"> *</span>}
          </Form.Label>
        )}

        <InputGroup>
          {prefix && (
            <InputGroup.Text className="rounded-start-3 rounded-end-0">
              {prefix}
            </InputGroup.Text>
          )}
          <Form.Control {...controlProps} />
          {suffix && (
            <InputGroup.Text className="rounded-start-0 rounded-end-3">
              {suffix}
            </InputGroup.Text>
          )}
        </InputGroup>

        {showFeedback && !noFeedback && (
          <Form.Control.Feedback
            type="invalid"
            className="fst-italic"
            style={{
              display: "block",
              visibility: meta.touched && meta.error ? "visible" : "hidden",
              minHeight: "1rem",
            }}
          >
            {meta.error || " "}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    </Col>
  );
};

export default Input;
